import { useEffect, useState } from "react";
import type { User } from "oidc-client-ts";
import { completeLogin, getUser, login, logout } from "./auth";
import TranscriptList from "./components/TranscriptList";
import TranscriptViewer from "./components/TranscriptViewer";
import LabelPanel from "./components/LabelPanel";
import type { TranscriptEntry, AnnotationSelection } from "./types";
import { listTranscripts, getLabels, type LabelConfig } from "./api/s3";

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (import.meta.env.VITE_DEV_BYPASS_AUTH === "true") {
      setUser({ profile: { email: "dev@local" }, access_token: "dev-token" } as any);
      setReady(true);
      return;
    }
    const isCallback = new URLSearchParams(window.location.search).has("code");
    const init = isCallback
      ? completeLogin()
      : getUser().then((u) => u);
    init
      .then((u) => setUser(u))
      .finally(() => setReady(true));
  }, []);

  if (!ready) return null;
  if (!user) return <LoginScreen />;
  return <Shell user={user} />;
}

function Shell({ user }: { user: User }) {
  const [filter, setFilter] = useState<"pending" | "completed">("pending");
  const [transcripts, setTranscripts] = useState<TranscriptEntry[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeAnnotationId, setActiveAnnotationId] = useState<string | null>(null);
  const [selections, setSelections] = useState<AnnotationSelection[]>([]);
  const [labels, setLabels] = useState<LabelConfig>({ active: [], archived: [] });

  useEffect(() => {
    listTranscripts().then(setTranscripts);
    getLabels().then(setLabels);
  }, []);

  function handleSelectTranscript(fileId: string) {
    setSelectedId(fileId);
    setActiveAnnotationId(null);
    setSelections([]);
  }

  function refreshTranscripts() {
    listTranscripts().then(setTranscripts);
  }

  function handleCompleted() {
    setSelectedId(null);
    setActiveAnnotationId(null);
    setSelections([]);
    listTranscripts().then(setTranscripts);
  }

  const email = String(user.profile.email ?? "");

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      <header style={headerStyle}>
        <span style={{ fontWeight: 600 }}>eHawk Tagging Tool</span>
        <span style={{ fontSize: 13, color: "#666" }}>
          {email}&nbsp;&nbsp;
          <button onClick={() => logout()} style={logoutBtnStyle}>Sign out</button>
        </span>
      </header>

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        <TranscriptList
          transcripts={transcripts}
          filter={filter}
          selectedId={selectedId}
          onFilterChange={setFilter}
          onSelect={handleSelectTranscript}
        />

        <TranscriptViewer
          fileId={selectedId}
          username={email}
          activeAnnotationId={activeAnnotationId}
          selections={selections}
          onAnnotationSelect={setActiveAnnotationId}
          onSelectionsChange={setSelections}
          onDraftSaved={refreshTranscripts}
          onCompleted={handleCompleted}
        />

        <LabelPanel
          activeAnnotationId={activeAnnotationId}
          selections={selections}
          activeLabels={labels.active}
          archivedLabels={labels.archived}
          onLabelsChange={setLabels}
          onUpdate={(id, updatedLabels, reasoning) => {
            setSelections((prev) =>
              prev.map((s) => (s.id === id ? { ...s, labels: updatedLabels, reasoning } : s))
            );
          }}
          onClear={(id) => {
            setSelections((prev) => prev.filter((s) => s.id !== id));
            setActiveAnnotationId(null);
          }}
        />
      </div>
    </div>
  );
}


function LoginScreen() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
      <div style={{ textAlign: "center" }}>
        <h1 style={{ marginBottom: 24 }}>eHawk Tagging Tool</h1>
        <button onClick={() => login()} style={{ padding: "10px 24px", background: "#333", color: "#fff", border: "none", borderRadius: 4, fontSize: 15, cursor: "pointer" }}>
          Sign in
        </button>
      </div>
    </div>
  );
}

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 16px",
  borderBottom: "1px solid #e0e0e0",
  background: "#fff",
  flexShrink: 0,
};

const logoutBtnStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "#555",
  cursor: "pointer",
  padding: 0,
  fontSize: 13,
  textDecoration: "underline",
};
